import {
  AloftPrice,
  AloftTime,
  CompType,
  formatLongRevisionType,
  noop,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import {
  Block,
  Button,
  Flex,
  Icon,
  InfoIcon,
  RadioGroup,
  SimpleDollarInput,
  Tabs,
  Typography,
  colors,
  theme,
} from '@aloft/toolkit-sui';

import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, Form, FormProvider, SubmitHandler, useWatch } from 'react-hook-form';

import { FormTextarea } from '../../components/FormTextarea/FormTextarea.tsx';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { ApproachesToValueChart } from '../../components/graphs/reconciliation/ApproachesToValueChart.tsx';
import { ValidationErrorType } from '../../hooks/validation/types.ts';
import { useAppraisifyValidation } from '../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliation } from '../../redux/features/reconciliationSlice.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectEstimatedROI } from '../../redux/features/selectors/selectEstimatedROI.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { selectAllCompTypesReconciliationData } from '../../redux/features/selectors/selectReconciliationData.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { RequiredFieldAnchor } from '../alerts/RequiredFieldAnchor.tsx';
import { useRequiredFieldFocusEffect } from '../alerts/useRequiredFieldFocusEffect.ts';
import { ReconciliationSummarizedValues } from './components/ReconciliationSummarizedValues.tsx';
import { useDesktopReconciliationComparison } from './hooks/useDesktopReconciliationComparison.ts';
import { useReconciliationForm } from './hooks/useReconciliationForm.ts';
import { ReconciliationFormOutput } from './schemas.ts';
import { getOpinionOfValueOutsideAdjustedSalePriceRangeWarning } from './utils/getOpinionOfValueOutsideAdjustedSalePriceRangeWarning.ts';
import { getMostRecentPreviousCommentaryForComparable } from './utils/getPreviousAlternativeComparablesEntries.ts';
import { getRenovationBudgetBelowValueIncreaseWarning } from './utils/getRenovationBudgetBelowValueIncreaseWarning.ts';

const VALUE_TYPE_ITEMS = [
  {
    label: 'As-is',
    value: 'as-is',
  },
  {
    label:
      'Subject to the following repairs or alterations on the basis of a hypothetical condition that the repairs or alterations have been completed',
    value: 'subject-to',
  },
];

const RevisionTimelineRow = ({
  requestedAt,
  isLast,
  children,
}: PropsWithChildren<{
  requestedAt?: AloftTime;
  isLast?: boolean;
}>) => {
  const userTz = AloftTime.getCurrentTimeZone();
  const parsedRequestedAt = orUndefined(AloftTime.schema.parse)(requestedAt);
  const dateLine = parsedRequestedAt?.formatTime(userTz, 'MM/dd/yy');
  const timeLine = parsedRequestedAt?.formatTime(userTz, 'hh:mm aa zzz');

  return (
    <Flex gap="16px" alignItems="flex-start" paddingX="16px" paddingY="0">
      {/* Left: date + time */}
      <Flex
        flexDirection="column"
        gap={1}
        alignItems="flex-start"
        justifyContent="flex-start"
        paddingTop="8px"
        width="72px"
        flexShrink={0}
        __dangerDoNotUseSx={{
          textTransform: 'uppercase',
        }}
      >
        {dateLine && (
          <Typography
            variant="subtitle2"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontSize: '12px', lineHeight: 1 }}
          >
            {dateLine}
          </Typography>
        )}
        {timeLine && (
          <Typography
            variant="subtitle2"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontSize: '10px', lineHeight: 1 }}
          >
            {timeLine}
          </Typography>
        )}
      </Flex>

      {/* Center: icon + vertical line */}
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        paddingTop="8px"
        flexShrink={0}
      >
        <Icon icon="edit-box" color="alertWarning" size="md" />
        {!isLast && (
          <Flex
            flex="1 0 0"
            width="1px"
            minHeight="8px"
            backgroundColor="alertInfoLight"
            borderRadius="4px"
            marginTop="4px"
          />
        )}
      </Flex>

      {/* Right: content */}
      <Flex
        flex={1}
        flexDirection="column"
        gap={2}
        paddingTop="8px"
        paddingBottom="16px"
        minWidth={0}
      >
        {children}
      </Flex>
    </Flex>
  );
};

const DiscrepancyWarningIcon = () => (
  <Flex
    width="28px"
    height="28px"
    borderRadius="50%"
    alignItems="center"
    justifyContent="center"
    backgroundColor="statusWarning"
    flexShrink={0}
  >
    <Typography
      color="textOffset"
      __dangerDoNotUseSx={{
        fontSize: '22px',
        lineHeight: '1',
        fontWeight: 700,
      }}
    >
      !
    </Typography>
  </Flex>
);

type ReconciliationTab = 'opinions' | 'revision' | 'final';

const findActualScrollRoot = (start: HTMLElement | null): HTMLElement | Window => {
  let el: HTMLElement | null = start;
  while (el) {
    const { overflow, overflowY } = window.getComputedStyle(el);
    const scrollable =
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflow === 'auto' ||
      overflow === 'scroll';

    if (scrollable && el.scrollHeight > el.clientHeight + 1) return el;
    el = el.parentElement;
  }

  return window;
};

const isOutsideIndicatedCostThreshold = (indicated?: AloftPrice, median?: AloftPrice): boolean => {
  if (!indicated || !median || median.unitAmount === 0) return false;
  return Math.abs(indicated.unitAmount - median.unitAmount) / Math.abs(median.unitAmount) > 0.25;
};

const formatValue = (value?: AloftPrice) => (value ? value.toString(true) : 'N/A');
const discrepancyDirection = (delta?: AloftPrice) => {
  if (!delta) return '';
  if (delta.toDollarLike() > 0) return 'increase';
  if (delta.toDollarLike() < 0) return 'decrease';
  return '';
};

const discrepancyDifference = (delta?: AloftPrice) => {
  if (!delta) return '';
  return AloftPrice.fromDollarLike(Math.abs(delta.toDollarLike())).toString(true);
};

export const ReconciliationModule = () => {
  useRequiredFieldFocusEffect();
  const dispatch = useAppDispatch();
  const form = useReconciliationForm();
  const [activeTab, setActiveTab] = useState<ReconciliationTab>('opinions');
  const [opinionViewModes, setOpinionViewModes] = useState<{
    default: 'chart' | 'table';
    aiv: 'chart' | 'table';
    arv: 'chart' | 'table';
  }>({
    default: 'chart',
    aiv: 'chart',
    arv: 'chart',
  });
  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const reconciliation = useAppSelector(selectReconciliation);
  const order = useAppSelector(selectOrder);
  const {
    isRevision,
    revisionData,
    previousRevisionsData,
    hasArvOption: isArvOrder,
    isMock: isMockOrder,
  } = order;
  const shouldShowRevisionTab = isRevision || !!revisionData?.revisionType;
  const previousAlternativeComparablesHistories =
    reconciliationCommentary?.previousAlternativeComparablesCommentaries;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<ReconciliationTab, HTMLDivElement | null>>({
    opinions: null,
    revision: null,
    final: null,
  });
  const watchedOpinionOfMarketValue = useWatch({
    control: form.control,
    name: 'opinionOfMarketValue',
  });
  const watchedOpinionOfAfterRepairValue = useWatch({
    control: form.control,
    name: 'opinionOfAfterRepairValue',
  });
  const watchedTotalRenovationBudget = useWatch({
    control: form.control,
    name: 'totalRenovationBudget',
  });
  const watchedOpinionOfMarketValuePrice = orUndefined(schAloftPrice.parse)(
    watchedOpinionOfMarketValue,
  );
  const watchedOpinionOfAfterRepairValuePrice = orUndefined(schAloftPrice.parse)(
    watchedOpinionOfAfterRepairValue,
  );
  const watchedTotalRenovationBudgetPrice = orUndefined(schAloftPrice.parse)(
    watchedTotalRenovationBudget,
  );
  const renovationBudgetWarningMessage = getRenovationBudgetBelowValueIncreaseWarning({
    opinionOfMarketValue: watchedOpinionOfMarketValuePrice,
    opinionOfAfterRepairValue: watchedOpinionOfAfterRepairValuePrice,
    totalRenovationBudget: watchedTotalRenovationBudgetPrice,
  });
  const reconciliationData = useAppSelector(selectAllCompTypesReconciliationData);
  const opinionOfMarketValueRangeWarningMessage =
    getOpinionOfValueOutsideAdjustedSalePriceRangeWarning({
      opinionOfValue: watchedOpinionOfMarketValuePrice,
      minCompMarketAdjustedSalePrice: reconciliationData.general.minCompMarketAdjustedSalePrice,
      maxCompMarketAdjustedSalePrice: reconciliationData.general.maxCompMarketAdjustedSalePrice,
      valueLabel: 'Opinion of Market Value',
    });
  const opinionOfAsIsValueRangeWarningMessage =
    getOpinionOfValueOutsideAdjustedSalePriceRangeWarning({
      opinionOfValue: watchedOpinionOfMarketValuePrice,
      minCompMarketAdjustedSalePrice: reconciliationData.aiv.minCompMarketAdjustedSalePrice,
      maxCompMarketAdjustedSalePrice: reconciliationData.aiv.maxCompMarketAdjustedSalePrice,
      valueLabel: 'Opinion of As-Is Value',
    });
  const opinionOfAfterRepairValueRangeWarningMessage =
    getOpinionOfValueOutsideAdjustedSalePriceRangeWarning({
      opinionOfValue: watchedOpinionOfAfterRepairValuePrice,
      minCompMarketAdjustedSalePrice: reconciliationData.arv.minCompMarketAdjustedSalePrice,
      maxCompMarketAdjustedSalePrice: reconciliationData.arv.maxCompMarketAdjustedSalePrice,
      valueLabel: 'Opinion of After-Repair Value',
    });
  const {
    isHybridOrder: isHybridDesktopComparison,
    priorDesktopAiv,
    priorDesktopArv,
    aivDelta,
    arvDelta,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
  } = useDesktopReconciliationComparison({
    opinionOfMarketValue: watchedOpinionOfMarketValuePrice,
    opinionOfAfterRepairValue: watchedOpinionOfAfterRepairValuePrice,
  });
  const revisionCommentaryLabel = `${formatLongRevisionType(
    revisionData?.revisionType || 'rov',
  )} Commentary`;
  const tabs = useMemo(
    () =>
      [
        { label: 'Opinions of Value', value: 'opinions' },
        ...(shouldShowRevisionTab ? [{ label: 'Revision / ROV', value: 'revision' }] : []),
        { label: 'Final Reconciliation', value: 'final' },
      ] as { label: string; value: ReconciliationTab }[],
    [shouldShowRevisionTab],
  );

  useEffect(() => {
    const savedRevisionCommentary = reconciliationCommentary?.revisionCommentary ?? '';
    const previousRevisionCommentary =
      reconciliationCommentary?.previousRevisionCommentaries?.[0] ?? '';
    const savedAlternativeComparablesCommentary =
      reconciliationCommentary?.alternativeComparablesCommentary ?? [];

    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      aivReconciliationCommentary:
        reconciliationCommentary?.aivReconciliationCommentary ??
        reconciliationCommentary?.reconciliationCommentary ??
        '',
      arvReconciliationCommentary: reconciliationCommentary?.arvReconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliationCommentary?.highestAndBestUseCommentary ?? '',
      totalCostToCure: orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure),
      totalCostToCureCommentary: reconciliationCommentary?.totalCostToCureCommentary ?? '',
      opinionOfMarketValue: orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue),
      opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
        reconciliation.opinionOfAfterRepairValue,
      ),
      totalRenovationBudget: orUndefined(schAloftPrice.parse)(reconciliation.totalRenovationBudget),
      revisionCommentary: savedRevisionCommentary.trim()
        ? savedRevisionCommentary
        : previousRevisionCommentary,
      alternativeComparablesCommentary:
        revisionData?.comparables?.map((comp, index) => {
          const saved = savedAlternativeComparablesCommentary[index] ?? '';
          if (saved.trim()) return saved;
          return getMostRecentPreviousCommentaryForComparable(
            previousAlternativeComparablesHistories,
            comp.address,
            index,
          );
        }) ?? savedAlternativeComparablesCommentary,
      feasibilityCommentary: reconciliationCommentary?.feasibilityCommentary ?? '',
      aivDiscrepancyCommentary: reconciliationCommentary?.aivDiscrepancyCommentary ?? '',
      arvDiscrepancyCommentary: reconciliationCommentary?.arvDiscrepancyCommentary ?? '',
      hasAivDiscrepancy,
      hasArvDiscrepancy,
      valueTypes: reconciliation.valueTypes,
    });
  }, [
    reconciliationCommentary,
    reconciliation,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
    revisionData?.comparables,
    previousAlternativeComparablesHistories,
  ]);

  useEffect(() => {
    form.setValue('hasAivDiscrepancy', hasAivDiscrepancy);
    form.setValue('hasArvDiscrepancy', hasArvDiscrepancy);
  }, [form, hasAivDiscrepancy, hasArvDiscrepancy]);

  const validation = useAppraisifyValidation();
  const {
    totalCostToCure: totalCostToCureError,
    totalCostToCureCommentary: totalCostToCureCommentaryError,
    opinionOfMarketValue: opinionOfMarketValueError,
    opinionOfAfterRepairValue: opinionOfAfterRepairValueError,
    totalRenovationBudget: totalRenovationBudgetError,
    highestAndBestUseCommentary: highestAndBestUseCommentaryError,
    reconciliationCommentary: reconciliationCommentaryError,
    aivReconciliationCommentary: aivReconciliationCommentaryError,
    arvReconciliationCommentary: arvReconciliationCommentaryError,
    revisionCommentary: revisionCommentaryError,
    alternativeComparablesCommentary: alternativeComparablesCommentaryError,
    feasibilityCommentary: feasibilityCommentaryError,
    aivDiscrepancyCommentary: aivDiscrepancyCommentaryError,
    arvDiscrepancyCommentary: arvDiscrepancyCommentaryError,
  } = validation.errors.Reconciliation;

  const showIndicatedCostWarningGeneral = useMemo(
    () =>
      !isArvOrder &&
      isOutsideIndicatedCostThreshold(
        reconciliationData.general.costApproachValue,
        reconciliationData.general.medianCompMarketAdjustedSalePrice,
      ),
    [isArvOrder, reconciliationData],
  );
  const showIndicatedCostWarningAiv = useMemo(
    () =>
      isArvOrder &&
      isOutsideIndicatedCostThreshold(
        reconciliationData.aiv.costApproachValue,
        reconciliationData.aiv.medianCompMarketAdjustedSalePrice,
      ),
    [isArvOrder, reconciliationData],
  );
  const showIndicatedCostWarningArv = useMemo(
    () =>
      isArvOrder &&
      isOutsideIndicatedCostThreshold(
        reconciliationData.arv.costApproachValue,
        reconciliationData.arv.medianCompMarketAdjustedSalePrice,
      ),
    [isArvOrder, reconciliationData],
  );

  const estimatedROI = isArvOrder ? useAppSelector(selectEstimatedROI) : undefined;

  const shouldShowFeasibilityCommentary = useMemo(() => {
    if (!estimatedROI) return false;
    return estimatedROI.toDollarLike() <= 0;
  }, [estimatedROI]);

  useEffect(() => {
    dispatch(setSelectedDataSheet('pdf'));
    dispatch(setReferenceViewOpen(true));
  }, []);

  const onSubmit: SubmitHandler<ReconciliationFormOutput> = (data) => {
    if (isArvOrder) {
      dispatch(
        submitReconciliation({
          ...data,
          reconciliationCommentary: data.aivReconciliationCommentary,
        }),
      );
    } else {
      dispatch(
        submitReconciliation({
          ...data,
          aivReconciliationCommentary: undefined,
          arvReconciliationCommentary: undefined,
        }),
      );
    }
  };

  const handleChange = () => {
    form.handleSubmit(onSubmit, undefined)().catch(noop);
  };

  const valueTypesSubHeader = (
    <Flex
      borderColor="strokeDefault"
      borderStyle="solid"
      borderWidth="1px"
      borderRadius="5px"
      padding="15px"
      flexDirection="column"
      marginBottom="15px"
      gap={4}
      backgroundColor="surface1"
    >
      <Typography variant="body1" color="textSecondary">
        This appraisal is made:
      </Typography>
      <Controller
        name="valueTypes"
        control={form.control}
        render={({ field }) => (
          <RequiredFieldAnchor
            fieldName="valueTypes"
            isFieldComplete={Boolean(field.value?.length)}
          >
            <RadioGroup
              name="valueType"
              items={VALUE_TYPE_ITEMS}
              onChange={(value) => {
                field.onChange([value]);
                handleChange();
              }}
              value={field.value?.[0] ?? ''}
              size="md"
              textVariant="h5"
              gap="8px"
            />
          </RequiredFieldAnchor>
        )}
      />
    </Flex>
  );

  let roiDisplay = 'TBD';
  if (estimatedROI) {
    roiDisplay =
      estimatedROI.toDollarLike() < 0
        ? `-$${Math.abs(estimatedROI.toDollarLike()).toLocaleString()}`
        : estimatedROI.toString(true);
  }

  let roiDisplayColor = theme.colors.hoverTransparent;
  if (estimatedROI) {
    roiDisplayColor =
      estimatedROI.toDollarLike() < 0 ? theme.colors.textError : theme.colors.textSuccess;
  }

  const renderChartOrSummary = (compType?: CompType) => {
    const modeKey = compType ?? 'default';
    const opinionViewMode = opinionViewModes[modeKey];
    const setOpinionViewMode = (mode: 'chart' | 'table') =>
      setOpinionViewModes((prev) => ({ ...prev, [modeKey]: mode }));
    const switcher = (
      <Flex
        border="2px solid surfaceAccent7"
        borderRadius="8px"
        overflow="hidden"
        width="fit-content"
        backgroundColor="surface1"
      >
        <Button
          type="button"
          variant="tertiary"
          text="Chart"
          size="sm"
          onClick={() => setOpinionViewMode('chart')}
          __dangerDoNotUseSx={{
            borderRadius: 0,
            borderRight: `1px solid ${theme.colors.surfaceAccent1}`,
            backgroundColor:
              opinionViewMode === 'chart' ? theme.colors.surfaceAccent5 : theme.colors.surface1,
            color: theme.colors.surfaceAccent1,
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '20px',
            padding: '8px 20px',
          }}
        />
        <Button
          type="button"
          variant="tertiary"
          text="Table"
          size="sm"
          onClick={() => setOpinionViewMode('table')}
          __dangerDoNotUseSx={{
            borderRadius: 0,
            backgroundColor:
              opinionViewMode === 'table' ? theme.colors.surfaceAccent5 : theme.colors.surface1,
            color: theme.colors.surfaceAccent1,
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '24px',
            padding: '4px 10px',
          }}
        />
      </Flex>
    );

    if (opinionViewMode === 'chart') {
      return <ApproachesToValueChart compType={compType} inlineSwitcher={switcher} />;
    }
    return <ReconciliationSummarizedValues compType={compType} inlineSwitcher={switcher} />;
  };

  useEffect(() => {
    const inner = scrollContainerRef.current;
    if (!inner) return;

    const scrollRoot = findActualScrollRoot(inner);

    const handleScroll = () => {
      const containerRect =
        scrollRoot instanceof Window
          ? new DOMRect(0, 0, window.innerWidth, window.innerHeight)
          : scrollRoot.getBoundingClientRect();
      const clientHeight =
        scrollRoot instanceof Window ? window.innerHeight : scrollRoot.clientHeight;
      const scrollHeight =
        scrollRoot instanceof Window
          ? document.documentElement.scrollHeight
          : scrollRoot.scrollHeight;
      const scrollTop = scrollRoot instanceof Window ? window.scrollY : scrollRoot.scrollTop;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 1;

      if (isAtBottom) {
        const lastTab = tabs.at(-1);
        if (lastTab) setActiveTab(lastTab.value);
        return;
      }

      const entries = tabs
        .map(({ value }) => ({
          tab: value,
          el: sectionRefs.current[value],
        }))
        .filter((entry): entry is { tab: ReconciliationTab; el: HTMLDivElement } => !!entry.el)
        .map(({ tab, el }) => ({
          tab,
          rect: el.getBoundingClientRect(),
        }))
        .filter(({ rect }) => rect.top + rect.height / 4 >= containerRect.top)
        .map(({ tab, rect }) => ({
          tab,
          distance: Math.abs(rect.top - containerRect.top),
        }))
        .sort((a, b) => a.distance - b.distance);
      const nextActive = entries[0];
      if (nextActive) {
        setActiveTab(nextActive.tab);
      }
    };

    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollRoot.removeEventListener('scroll', handleScroll);
  }, [tabs]);

  const handleTabOnClick = (tab: ReconciliationTab) => {
    setActiveTab(tab);
    sectionRefs.current[tab]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Flex {...useTkv2ReferenceBarEvents()} width="100%">
      <FormProvider {...form}>
        <Form style={{ width: '100%' }} onChange={handleChange}>
          <Flex
            margin="15px"
            flexDirection="column"
            gap={4}
            flex={1}
            width="95%"
            minWidth="600px"
            maxWidth="900px"
            paddingBottom="50px"
          >
            <Flex
              position="sticky"
              top={0}
              zIndex={3}
              backgroundColor="surface1"
              paddingTop="4px"
              paddingBottom="4px"
              flexDirection="column"
              gap={2}
            >
              <Typography variant="h1">Reconciliation</Typography>
              <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={handleTabOnClick} />
            </Flex>
            <Flex
              ref={scrollContainerRef}
              flexDirection="column"
              gap={4}
              overflow="auto"
              minHeight={0}
            >
              <Flex
                ref={(el) => {
                  sectionRefs.current.opinions = el;
                }}
                backgroundColor={activeTab === 'opinions' ? 'surface3' : 'surface1'}
                borderRadius="5px"
                padding="12px"
                flexDirection="column"
                gap={4}
              >
                <Typography variant="h1">Opinions of Value</Typography>
                {!isArvOrder && reconciliation?.includeTotalCostToCure && (
                  <Flex flexDirection="column">
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Flex alignItems="center" gap="4px">
                        <Typography variant="h3" color="textSecondary">
                          Total Cost to Cure
                          <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                            {!isMockOrder && '*'}
                          </span>
                        </Typography>
                        <InfoIcon tooltipContent="Major items only (i.e. soundness, security, livability). No cosmetic or minor deferred maintenance." />
                      </Flex>
                      <Controller
                        name="totalCostToCure"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <RequiredFieldAnchor
                            fieldName="totalCostToCure"
                            isFieldComplete={field.value != null}
                          >
                            <SimpleDollarInput
                              value={field.value ? field.value.toString(true, true) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value
                                  ? AloftPrice.fromDollarLike(e.target.value)
                                  : undefined;
                                field.onChange(value);
                                handleChange();
                              }}
                              onBlur={field.onBlur}
                              required={!isMockOrder}
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Required. Please describe the major items (i.e. soundness, security, livability) that account for this cost to cure estimation. Do not include cosmetic or minor deferred maintenance.'
                              }
                              width="300px"
                              errorMessage={
                                fieldState.isTouched ? totalCostToCureError?.message : undefined
                              }
                              invalid={fieldState.invalid && fieldState.isTouched}
                            />
                          </RequiredFieldAnchor>
                        )}
                      />
                    </Flex>
                    <Controller
                      name="totalCostToCureCommentary"
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name="totalCostToCureCommentary"
                          requiredFieldName="totalCostToCureCommentary"
                          control={form.control}
                          placeholder={`${!isMockOrder ? 'Required' : 'Optional'}. Please describe the major items (i.e. soundness, security, livability) that account for this cost to cure estimation. Do not include cosmetic or minor deferred maintenance.`}
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          height="200px"
                          labelColor="textSecondary"
                          size="sm"
                          resize="vertical"
                          customErrorMessage={
                            fieldState.isTouched
                              ? totalCostToCureCommentaryError?.message
                              : undefined
                          }
                          column
                        />
                      )}
                    />
                  </Flex>
                )}
                {isArvOrder && (
                  <Flex flexDirection="column" gap={2}>
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Block>
                        <Typography variant="h3" color="textSecondary">
                          Renovation Budget
                          <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                            {!isMockOrder && '*'}
                          </span>
                        </Typography>
                      </Block>
                      <Controller
                        name="totalRenovationBudget"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <RequiredFieldAnchor
                            fieldName="totalRenovationBudget"
                            isFieldComplete={field.value != null}
                          >
                            <SimpleDollarInput
                              value={field.value ? field.value.toString(true, true) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value
                                  ? AloftPrice.fromDollarLike(e.target.value)
                                  : undefined;
                                field.onChange(value);
                                handleChange();
                              }}
                              onBlur={field.onBlur}
                              required={!isMockOrder}
                              placeholder={isMockOrder ? 'Optional' : 'Required'}
                              width="300px"
                              errorMessage={
                                fieldState.isTouched
                                  ? totalRenovationBudgetError?.message
                                  : undefined
                              }
                              invalid={fieldState.invalid && fieldState.isTouched}
                              warning={!!renovationBudgetWarningMessage}
                            />
                          </RequiredFieldAnchor>
                        )}
                      />
                    </Flex>
                    {renovationBudgetWarningMessage && (
                      <Flex alignItems="center" gap={2}>
                        <Icon icon="error-warning" color="alertWarning" />
                        <Typography variant="h5" color="textPrimary">
                          {renovationBudgetWarningMessage}
                        </Typography>
                      </Flex>
                    )}
                  </Flex>
                )}
                {!isArvOrder && (
                  <Flex flexDirection="column" gap={2}>
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Block>
                        <Typography variant="h3" color="textSecondary">
                          Opinion of Market Value
                          <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                            {!isMockOrder && '*'}
                          </span>
                        </Typography>
                        {priorDesktopAiv && (
                          <Typography variant="body2" color="textSecondary">
                            Previous Desktop Value: {formatValue(priorDesktopAiv)}
                          </Typography>
                        )}
                      </Block>
                      <Controller
                        name="opinionOfMarketValue"
                        control={form.control}
                        render={({ field }) => (
                          <RequiredFieldAnchor
                            fieldName="opinionOfMarketValue"
                            isFieldComplete={field.value != null}
                          >
                            <SimpleDollarInput
                              value={field.value ? field.value.toString(true, true) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value
                                  ? AloftPrice.fromDollarLike(e.target.value)
                                  : undefined;
                                field.onChange(value);
                                handleChange();
                              }}
                              onBlur={field.onBlur}
                              required={!isMockOrder}
                              placeholder={isMockOrder ? 'Optional' : 'Required'}
                              width="300px"
                              errorMessage={
                                opinionOfMarketValueError?.type === ValidationErrorType.INVALID
                                  ? opinionOfMarketValueError?.message
                                  : ''
                              }
                              warning={!!opinionOfMarketValueRangeWarningMessage}
                            />
                          </RequiredFieldAnchor>
                        )}
                      />
                    </Flex>
                    {opinionOfMarketValueRangeWarningMessage && (
                      <Flex alignItems="center" gap={2}>
                        <Icon icon="error-warning" color="alertWarning" />
                        <Typography variant="h5" color="textPrimary">
                          {opinionOfMarketValueRangeWarningMessage}
                        </Typography>
                      </Flex>
                    )}
                  </Flex>
                )}
                {!isArvOrder && showIndicatedCostWarningGeneral && (
                  <Flex alignItems="flex-start" gap={2}>
                    <Icon icon="error-warning" color="alertWarning" size="md" />
                    <Typography
                      variant="h5"
                      color="textPrimary"
                      __dangerDoNotUseSx={{ lineHeight: '100%' }}
                    >
                      Indicated Cost Value beyond 25% of Median Comp Adjusted Sale Price. Align the
                      two approaches, or explain the difference between the Cost and Sales
                      Comparison approaches.
                    </Typography>
                  </Flex>
                )}
                {!isArvOrder && renderChartOrSummary()}
                {!isArvOrder && hasAivDiscrepancy && (
                  <Flex flexDirection="column" gap={3}>
                    <Flex alignItems="center" gap={2}>
                      <DiscrepancyWarningIcon />
                      <Typography
                        variant="h5"
                        color="textPrimary"
                        __dangerDoNotUseSx={{ lineHeight: '100%' }}
                      >
                        Your Opinion of Market Value differs between this current Hybrid appraisal
                        and the previous Desktop Appraisal. Explain the difference in the Value
                        Discrepancy Commentary below.
                      </Typography>
                    </Flex>
                    <Controller
                      name="aivDiscrepancyCommentary"
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name="aivDiscrepancyCommentary"
                          requiredFieldName="aivDiscrepancyCommentary"
                          control={form.control}
                          label="Value Discrepancy Commentary"
                          placeholder={
                            isMockOrder
                              ? 'Optional'
                              : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                          }
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          labelColor="textSecondary"
                          labelVariant="h3"
                          size="sm"
                          resize="vertical"
                          column
                          customErrorMessage={
                            fieldState.isTouched
                              ? aivDiscrepancyCommentaryError?.message
                              : undefined
                          }
                        />
                      )}
                    />
                  </Flex>
                )}
                {isArvOrder && (
                  <>
                    <Flex flexDirection="column" gap={2}>
                      <Flex flex={1} justifyContent="space-between" alignItems="center">
                        <Block>
                          <Flex alignItems="center" gap={2}>
                            <Typography variant="h3" color="textSecondary">
                              Opinion of As-Is Market Value
                              <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                                {!isMockOrder && '*'}
                              </span>
                            </Typography>
                            <Typography
                              variant="body1"
                              color="aivText"
                              __dangerDoNotUseSx={{
                                textTransform: 'uppercase',
                                backgroundColor: colors.aiv,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                                lineHeight: '17px',
                              }}
                            >
                              AIV
                            </Typography>
                          </Flex>
                          {isHybridDesktopComparison && priorDesktopAiv && (
                            <Typography variant="body2" color="textSecondary">
                              Previous Desktop AIV: {formatValue(priorDesktopAiv)}
                            </Typography>
                          )}
                        </Block>
                        <Controller
                          name="opinionOfMarketValue"
                          control={form.control}
                          render={({ field }) => (
                            <RequiredFieldAnchor
                              fieldName="opinionOfMarketValue"
                              isFieldComplete={field.value != null}
                            >
                              <SimpleDollarInput
                                value={field.value ? field.value.toString(true, true) : ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const trimmedValue = e.target.value.trim();
                                  const value = orUndefined(AloftPrice.fromDollarLike)(
                                    trimmedValue,
                                  );

                                  field.onChange(value);
                                  handleChange();
                                }}
                                onBlur={field.onBlur}
                                required
                                placeholder="Required"
                                width="300px"
                                errorMessage={
                                  opinionOfMarketValueError?.type === ValidationErrorType.INVALID
                                    ? opinionOfMarketValueError?.message
                                    : ''
                                }
                                warning={!!opinionOfAsIsValueRangeWarningMessage}
                              />
                            </RequiredFieldAnchor>
                          )}
                        />
                      </Flex>
                      {opinionOfAsIsValueRangeWarningMessage && (
                        <Flex alignItems="center" gap={2}>
                          <Icon icon="error-warning" color="alertWarning" />
                          <Typography variant="h5" color="textPrimary">
                            {opinionOfAsIsValueRangeWarningMessage}
                          </Typography>
                        </Flex>
                      )}
                    </Flex>
                    {renderChartOrSummary('aiv')}
                    {showIndicatedCostWarningAiv && (
                      <Flex alignItems="flex-start" gap={2}>
                        <Icon icon="error-warning" color="alertWarning" size="md" />
                        <Typography
                          variant="h5"
                          color="textPrimary"
                          __dangerDoNotUseSx={{ lineHeight: '100%' }}
                        >
                          <Typography
                            variant="body1"
                            color="aivText"
                            __dangerDoNotUseSx={{
                              display: 'inline',
                              backgroundColor: colors.aiv,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              marginRight: '6px',
                              textTransform: 'uppercase',
                            }}
                          >
                            AIV
                          </Typography>
                          Indicated Cost Value beyond 25% of Median Comp Adjusted Sale Price. Align
                          the two approaches, or explain the difference between the Cost and Sales
                          Comparison approaches.
                        </Typography>
                      </Flex>
                    )}
                    {hasAivDiscrepancy && (
                      <Flex flexDirection="column" gap={3}>
                        <Flex alignItems="center" gap={2}>
                          <DiscrepancyWarningIcon />
                          <Typography
                            variant="h5"
                            color="textPrimary"
                            __dangerDoNotUseSx={{ lineHeight: '100%' }}
                          >
                            Previous Desktop AIV was {formatValue(priorDesktopAiv)} and Hybrid AIV
                            is {formatValue(watchedOpinionOfMarketValuePrice)}. This is a{' '}
                            {discrepancyDifference(aivDelta)} {discrepancyDirection(aivDelta)}.
                            Provide detailed commentary explaining the difference.
                          </Typography>
                        </Flex>
                        <Controller
                          name="aivDiscrepancyCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="aivDiscrepancyCommentary"
                              requiredFieldName="aivDiscrepancyCommentary"
                              control={form.control}
                              label="AIV Discrepancy Commentary"
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                              }
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              column
                              customErrorMessage={
                                fieldState.isTouched
                                  ? aivDiscrepancyCommentaryError?.message
                                  : undefined
                              }
                            />
                          )}
                        />
                      </Flex>
                    )}
                    <Flex flexDirection="column" gap={2}>
                      <Flex flex={1} justifyContent="space-between" alignItems="center">
                        <Block>
                          <Flex alignItems="center" gap={2}>
                            <Typography variant="h3" color="textSecondary">
                              Opinion of After-Repair Market Value
                              <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                                {!isMockOrder && '*'}
                              </span>
                            </Typography>
                            <Typography
                              variant="body1"
                              color="textOffset"
                              __dangerDoNotUseSx={{
                                textTransform: 'uppercase',
                                backgroundColor: colors.arv,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                                lineHeight: '17px',
                              }}
                            >
                              ARV
                            </Typography>
                          </Flex>
                          {isHybridDesktopComparison && priorDesktopArv && (
                            <Typography variant="body2" color="textSecondary">
                              Previous Desktop ARV: {formatValue(priorDesktopArv)}
                            </Typography>
                          )}
                        </Block>
                        <Controller
                          name="opinionOfAfterRepairValue"
                          control={form.control}
                          render={({ field }) => (
                            <RequiredFieldAnchor
                              fieldName="opinionOfAfterRepairValue"
                              isFieldComplete={field.value != null}
                            >
                              <SimpleDollarInput
                                value={field.value ? field.value.toString(true, true) : ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const trimmedValue = e.target.value.trim();
                                  const value = trimmedValue
                                    ? AloftPrice.fromDollarLike(trimmedValue)
                                    : undefined;
                                  field.onChange(value);
                                  handleChange();
                                }}
                                onBlur={field.onBlur}
                                required
                                placeholder="Required"
                                width="300px"
                                errorMessage={
                                  opinionOfAfterRepairValueError?.type ===
                                  ValidationErrorType.INVALID
                                    ? opinionOfAfterRepairValueError?.message
                                    : ''
                                }
                                warning={!!opinionOfAfterRepairValueRangeWarningMessage}
                              />
                            </RequiredFieldAnchor>
                          )}
                        />
                      </Flex>
                      {opinionOfAfterRepairValueRangeWarningMessage && (
                        <Flex alignItems="center" gap={2}>
                          <Icon icon="error-warning" color="alertWarning" />
                          <Typography variant="h5" color="textPrimary">
                            {opinionOfAfterRepairValueRangeWarningMessage}
                          </Typography>
                        </Flex>
                      )}
                    </Flex>
                    {renderChartOrSummary('arv')}
                    {showIndicatedCostWarningArv && (
                      <Flex alignItems="flex-start" gap={2}>
                        <Icon icon="error-warning" color="alertWarning" size="md" />
                        <Typography
                          variant="h5"
                          color="textPrimary"
                          __dangerDoNotUseSx={{ lineHeight: '100%' }}
                        >
                          <Typography
                            variant="body1"
                            color="arvText"
                            __dangerDoNotUseSx={{
                              display: 'inline',
                              backgroundColor: colors.arv,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              marginRight: '6px',
                              textTransform: 'uppercase',
                            }}
                          >
                            ARV
                          </Typography>
                          Indicated Cost Value beyond 25% of Median Comp Adjusted Sale Price. Align
                          the two approaches, or explain the difference between the Cost and Sales
                          Comparison approaches.
                        </Typography>
                      </Flex>
                    )}
                    {hasArvDiscrepancy && (
                      <Flex flexDirection="column" gap={3}>
                        <Flex alignItems="center" gap={2}>
                          <DiscrepancyWarningIcon />
                          <Typography
                            variant="h5"
                            color="textPrimary"
                            __dangerDoNotUseSx={{ lineHeight: '100%' }}
                          >
                            Previous Desktop ARV was {formatValue(priorDesktopArv)} and Hybrid ARV
                            is {formatValue(watchedOpinionOfAfterRepairValuePrice)}. This is a{' '}
                            {discrepancyDifference(arvDelta)} {discrepancyDirection(arvDelta)}.
                            Provide detailed commentary explaining the difference.
                          </Typography>
                        </Flex>
                        <Controller
                          name="arvDiscrepancyCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="arvDiscrepancyCommentary"
                              requiredFieldName="arvDiscrepancyCommentary"
                              control={form.control}
                              label="ARV Discrepancy Commentary"
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                              }
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              column
                              customErrorMessage={
                                fieldState.isTouched
                                  ? arvDiscrepancyCommentaryError?.message
                                  : undefined
                              }
                            />
                          )}
                        />
                      </Flex>
                    )}
                    <Flex flex={1} justifyContent="space-between" alignItems="center">
                      <Block>
                        <Typography variant="h3" color="textLight">
                          Estimated Return on Investment
                        </Typography>
                        <Typography variant="body2" color="textLight">
                          {'\u{2248} After-Repair Value - (Renovation Budget + As-Is Value)'}
                        </Typography>
                      </Block>
                      <Flex flexDirection="column" alignItems="flex-end" justifyContent="center">
                        <Typography variant="body1" __dangerDoNotUseSx={{ color: roiDisplayColor }}>
                          {roiDisplay}
                        </Typography>
                        <Typography variant="body2" color="textLight">
                          {`\u{2248} ${formatValue(watchedOpinionOfAfterRepairValuePrice)} - (${formatValue(
                            watchedTotalRenovationBudgetPrice,
                          )} + ${formatValue(watchedOpinionOfMarketValuePrice)})`}
                        </Typography>
                      </Flex>
                    </Flex>
                    {shouldShowFeasibilityCommentary && (
                      <Flex flex={1} flexDirection="column">
                        <Controller
                          name="feasibilityCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="feasibilityCommentary"
                              requiredFieldName="feasibilityCommentary"
                              control={form.control}
                              label="Feasibility Commentary"
                              placeholder={
                                isMockOrder
                                  ? 'Optional'
                                  : 'Required. Provide a narrative explanation for why you believe the ROI on this property is negative. Reminder that the AIV reflects current conditions, while the ARV reflects post-repair conditions.'
                              }
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              height="200px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              customErrorMessage={
                                fieldState.isTouched
                                  ? feasibilityCommentaryError?.message
                                  : undefined
                              }
                              column
                            />
                          )}
                        />
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
              {shouldShowRevisionTab && (
                <>
                  <Typography variant="h1">Revision / ROV</Typography>
                  <Flex
                    ref={(el) => {
                      sectionRefs.current.revision = el;
                    }}
                    backgroundColor={activeTab === 'revision' ? 'surface3' : 'surface1'}
                    borderRadius="5px"
                    padding="12px"
                    flexDirection="column"
                    gap={0}
                  >
                    {/* Request description */}
                    <Flex
                      alignItems="center"
                      gap="5px"
                      marginBottom="12px"
                      padding="8px"
                      borderRadius="5px"
                      borderColor="strokeDefault"
                      borderStyle="solid"
                      borderWidth="1px"
                      backgroundColor="surface6"
                    >
                      <Typography variant="h4" color="textSecondary">
                        Request Description:
                      </Typography>
                      <Typography
                        variant="placeholder"
                        color="textSecondary"
                        __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {revisionData?.requestDescription || ''}
                      </Typography>
                    </Flex>

                    {/* Current revision — editable */}
                    <RevisionTimelineRow
                      requestedAt={revisionData?.requestedAt}
                      isLast={
                        !reconciliationCommentary?.previousRevisionCommentaries?.some(
                          (c) => c.trim().length > 0,
                        )
                      }
                    >
                      <Flex flexDirection="column" gap={2}>
                        <Controller
                          name="revisionCommentary"
                          control={form.control}
                          render={({ fieldState }) => (
                            <FormTextarea
                              name="revisionCommentary"
                              requiredFieldName="revisionCommentary"
                              control={form.control}
                              label={revisionCommentaryLabel}
                              placeholder={isMockOrder ? 'Optional' : 'Required'}
                              isRequired={!isMockOrder}
                              labelWidth="250px"
                              inputWidth="500px"
                              height="200px"
                              labelColor="textSecondary"
                              labelVariant="h3"
                              size="sm"
                              resize="vertical"
                              customErrorMessage={
                                fieldState.isTouched ? revisionCommentaryError?.message : undefined
                              }
                              column
                            />
                          )}
                        />
                        {revisionData?.comparables?.map((comp, index) => (
                          <Flex flex={1} flexDirection="column" key={comp.address} gap={2}>
                            <Typography variant="h3" color="textSecondary">
                              <>
                                {`Alternative Comparable ${index + 1}: ${comp.address}`}
                                {!isMockOrder ? <span style={{ color: 'red' }}> *</span> : null}
                              </>
                            </Typography>
                            <Controller
                              name={`alternativeComparablesCommentary.${index}` as const}
                              control={form.control}
                              render={({ fieldState }) => (
                                <FormTextarea
                                  name={`alternativeComparablesCommentary.${index}` as const}
                                  requiredFieldName="alternativeComparablesCommentary"
                                  control={form.control}
                                  placeholder={isMockOrder ? 'Optional' : 'Required'}
                                  isRequired={!isMockOrder}
                                  labelWidth="250px"
                                  inputWidth="500px"
                                  labelColor="textSecondary"
                                  labelVariant="h3"
                                  size="sm"
                                  resize="vertical"
                                  column
                                  customErrorMessage={
                                    fieldState.isTouched &&
                                    reconciliationCommentary?.alternativeComparablesCommentary?.[
                                      index
                                    ] === ''
                                      ? alternativeComparablesCommentaryError?.message
                                      : undefined
                                  }
                                />
                              )}
                            />
                          </Flex>
                        ))}
                      </Flex>
                    </RevisionTimelineRow>

                    {/* Previous revisions — read-only, oldest last */}
                    {(reconciliationCommentary?.previousRevisionCommentaries ?? [])
                      .map((commentary, index) => ({
                        commentary,
                        revisionType: previousRevisionsData?.[index]?.revisionType ?? 'revision',
                        requestedAt: previousRevisionsData?.[index]?.requestedAt,
                        altComps:
                          reconciliationCommentary?.previousAlternativeComparablesCommentaries?.[
                            index
                          ]?.comparables ?? [],
                      }))
                      .filter(({ commentary }) => commentary.trim().length > 0)
                      .reverse()
                      .map(
                        ({ commentary, revisionType: prevType, requestedAt, altComps }, i, arr) => (
                          <RevisionTimelineRow
                            key={i}
                            requestedAt={requestedAt}
                            isLast={i === arr.length - 1}
                          >
                            <Flex flexDirection="column" gap={2}>
                              <Typography variant="h3" color="textSecondary">
                                {`${formatLongRevisionType(prevType)} Commentary`}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="textSecondary"
                                __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                              >
                                {commentary}
                              </Typography>
                              {altComps.map((comp, altIndex) => (
                                <Flex
                                  key={altIndex}
                                  gap="16px"
                                  paddingLeft="0"
                                  alignItems="flex-start"
                                >
                                  <Flex
                                    flexDirection="column"
                                    alignItems="flex-start"
                                    justifyContent="center"
                                    paddingTop="4px"
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      color="textTertiary"
                                      __dangerDoNotUseSx={{
                                        textTransform: 'uppercase',
                                        width: '72px',
                                      }}
                                    >
                                      {`Alt. Comp. ${altIndex + 1}`}
                                    </Typography>
                                  </Flex>
                                  <Flex flexDirection="column" gap={1}>
                                    <Typography variant="h4" color="textSecondary">
                                      {comp.address}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                      __dangerDoNotUseSx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {comp.commentary}
                                    </Typography>
                                  </Flex>
                                </Flex>
                              ))}
                            </Flex>
                          </RevisionTimelineRow>
                        ),
                      )}
                  </Flex>
                </>
              )}
              <Flex
                ref={(el) => {
                  sectionRefs.current.final = el;
                }}
                backgroundColor={activeTab === 'final' ? 'surface3' : 'surface1'}
                borderRadius="5px"
                padding="12px"
                flexDirection="column"
                gap={4}
              >
                <Typography variant="h1">Final Reconciliation</Typography>
                <Flex flex={1} flexDirection="column">
                  {isArvOrder ? (
                    <>
                      {valueTypesSubHeader}
                      <Controller
                        name="aivReconciliationCommentary"
                        control={form.control}
                        render={({ fieldState }) => (
                          <FormTextarea
                            name="aivReconciliationCommentary"
                            requiredFieldName="aivReconciliationCommentary"
                            control={form.control}
                            label="AIV Reconciliation Commentary"
                            placeholder={isMockOrder ? 'Optional' : 'Required'}
                            isRequired={!isMockOrder}
                            labelWidth="250px"
                            inputWidth="500px"
                            labelColor="textSecondary"
                            labelVariant="h3"
                            size="sm"
                            resize="vertical"
                            height="200px"
                            customErrorMessage={
                              fieldState.isTouched
                                ? aivReconciliationCommentaryError?.message
                                : undefined
                            }
                            column
                          />
                        )}
                      />
                      <Controller
                        name="arvReconciliationCommentary"
                        control={form.control}
                        render={({ fieldState }) => (
                          <FormTextarea
                            name="arvReconciliationCommentary"
                            requiredFieldName="arvReconciliationCommentary"
                            control={form.control}
                            label="ARV Reconciliation Commentary"
                            placeholder={isMockOrder ? 'Optional' : 'Required'}
                            isRequired={!isMockOrder}
                            labelWidth="250px"
                            inputWidth="500px"
                            labelColor="textSecondary"
                            labelVariant="h3"
                            size="sm"
                            resize="vertical"
                            height="200px"
                            customErrorMessage={
                              fieldState.isTouched
                                ? arvReconciliationCommentaryError?.message
                                : undefined
                            }
                            column
                          />
                        )}
                      />
                    </>
                  ) : (
                    <Controller
                      name="reconciliationCommentary"
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name="reconciliationCommentary"
                          requiredFieldName="reconciliationCommentary"
                          control={form.control}
                          label="Reconciliation Commentary"
                          placeholder={isMockOrder ? 'Optional' : 'Required'}
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          labelColor="textSecondary"
                          labelVariant="h3"
                          size="sm"
                          resize="vertical"
                          height="300px"
                          customErrorMessage={
                            fieldState.isTouched
                              ? reconciliationCommentaryError?.message
                              : undefined
                          }
                          column
                          subHeaderContent={valueTypesSubHeader}
                        />
                      )}
                    />
                  )}
                </Flex>
                <Flex flex={1} flexDirection="column">
                  <Controller
                    name="highestAndBestUseCommentary"
                    control={form.control}
                    render={({ fieldState }) => (
                      <FormTextarea
                        name="highestAndBestUseCommentary"
                        requiredFieldName="highestAndBestUseCommentary"
                        control={form.control}
                        label="Highest & Best Use Commentary"
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        isRequired={!isMockOrder}
                        height={isArvOrder ? '200px' : '300px'}
                        labelWidth="250px"
                        inputWidth="500px"
                        labelColor="textSecondary"
                        labelVariant="h3"
                        size="sm"
                        resize="vertical"
                        customErrorMessage={
                          fieldState.isTouched
                            ? highestAndBestUseCommentaryError?.message
                            : undefined
                        }
                        column
                      />
                    )}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
